<template>
  <t-report-generate
      v-model="form"
      sub-validation-tag="sub-checklist-report-photos-occurrence-by-enterprise"
      modal
      default-pdf
      enable-generate-pdf
      enable-generate-word
      enable-setting
      origin="Teste de Origem"
      button-title="Relatório"
      :relation-key="form.enterpriseId"
      java-report
      title="DADOS DO RELATÓRIO">

    <template #default="{ status }">
      <div class="report-builder">

        <div class="report-section__title">
          Empreendimento
        </div>

        <t-input-code-label
            hide-label
            :command="'insert'"
            :status="status"
            required
            enterprise
            v-model="form.enterpriseId"
            @onResume="onEnterpriseResume"
        />

        <section class="report-section">
          <div class="report-section__title">
            Apresentação dos apontamentos
          </div>
          <div class="report-section__subtitle">
            Escolha como os apontamentos e fotos serão exibidos.
          </div>

          <b-row>
            <b-col
                v-for="option in ReportOccurrenceLayoutType.list"
                :key="option.value"
                cols="12"
                sm="6"
                xl="3"
                class="mb-3 d-flex"
            >
              <t-report-option-card
                  class="w-100"
                  :option="option"
                  :selected="form.reportLayout === option.value"
                  @select="form.reportLayout = option.value"
                  @preview="openPreview"
              />
            </b-col>
          </b-row>
        </section>

        <section class="report-section">

          <div class="report-section__title">
            Capa do Relatório
          </div>
          <div class="report-section__subtitle">
            Defina o estilo visual da capa
          </div>

          <b-row>
            <b-col
                v-for="option in coverOptions"
                :key="option.value"
                cols="12"
                md="6"
                xl="3"
                class="mb-3"
            >
              <t-report-option-card
                  :option="option"
                  :selected="form.coverLayout === option.value"
                  @select="form.coverLayout = option.value"
                  @preview="openPreview"
                  small
              />
            </b-col>
          </b-row>
        </section>

        <section class="report-section">
          <div class="report-section__header">
            <div class="report-section__title">
              Análises do relatório
            </div>
            <div class="report-section__subtitle">
              Escolha os recursos visuais e gerenciais incluídos no documento.
            </div>
          </div>

          <b-row>
            <b-col cols="12" lg="5">
              <div class="report-choice-group">
                <div class="report-choice-group__title">Gráficos</div>

                <div class="report-radio-cards">
                  <button
                      type="button"
                      class="report-radio-card"
                      :class="{ 'is-active': form.reportGraphic === ReportOccurrenceGraphicType.type.NONE }"
                      @click="form.reportGraphic = ReportOccurrenceGraphicType.type.NONE"
                  >
                    <div class="report-radio-card__icon">
                      <b-icon-list />
                    </div>
                    <div class="report-radio-card__content">
                      <div class="report-radio-card__title">Sem gráficos</div>
                      <div class="report-radio-card__desc">
                        Relatório apenas com os apontamentos.
                      </div>
                    </div>
                  </button>

                  <button
                      type="button"
                      class="report-radio-card"
                      :class="{ 'is-active': form.reportGraphic === ReportOccurrenceGraphicType.type.BASIC }"
                      @click="form.reportGraphic = ReportOccurrenceGraphicType.type.BASIC"
                  >
                    <div class="report-radio-card__icon">
                      <b-icon-pie-chart />
                    </div>
                    <div class="report-radio-card__content">
                      <div class="report-radio-card__title">Somente anomalias</div>
                      <div class="report-radio-card__desc">
                        Inclui gráficos focados em anomalias.
                      </div>
                    </div>
                  </button>

                  <button
                      type="button"
                      class="report-radio-card"
                      :class="{ 'is-active': form.reportGraphic === ReportOccurrenceGraphicType.type.FULL }"
                      @click="form.reportGraphic = ReportOccurrenceGraphicType.type.FULL"
                  >
                    <div class="report-radio-card__icon">
                      <b-icon-check-circle />
                    </div>
                    <div class="report-radio-card__content">
                      <div class="report-radio-card__title">Completo</div>
                      <div class="report-radio-card__desc">
                        Inclui todos os gráficos disponíveis.
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </b-col>

            <b-col cols="12" lg="7">
              <div class="report-choice-group">
                <div class="report-choice-group__title">Rankings</div>

                <div class="report-check-cards">
                  <button
                      type="button"
                      class="report-check-card"
                      :class="{ 'is-active': form.rankings.includes('discipline') }"
                      @click="toggleRanking('discipline')"
                  >
                    <div class="report-check-card__box">
                      <b-icon-check v-if="form.rankings.includes('discipline')" />
                    </div>
                    <div class="report-check-card__icon">
                      <b-icon-layers />
                    </div>
                    <div class="report-check-card__content">
                      <div class="report-check-card__title">Por Disciplina</div>
                      <div class="report-check-card__desc">
                        Organiza por especialidade (cívil, elétrica).
                      </div>
                    </div>
                  </button>

                  <button
                      type="button"
                      class="report-check-card"
                      :class="{ 'is-active': form.rankings.includes('criticality') }"
                      @click="toggleRanking('criticality')"
                  >
                    <div class="report-check-card__box">
                      <b-icon-check v-if="form.rankings.includes('criticality')" />
                    </div>
                    <div class="report-check-card__icon">
                      <b-icon-exclamation-triangle />
                    </div>
                    <div class="report-check-card__content">
                      <div class="report-check-card__title">Por Criticidade</div>
                      <div class="report-check-card__desc">
                        Prioriza níveis de risco.
                      </div>
                    </div>
                  </button>

                  <button
                      type="button"
                      class="report-check-card"
                      :class="{ 'is-active': form.rankings.includes('anomalies') }"
                      @click="toggleRanking('anomalies')"
                  >
                    <div class="report-check-card__box">
                      <b-icon-check v-if="form.rankings.includes('anomalies')" />
                    </div>
                    <div class="report-check-card__icon">
                      <b-icon-bar-chart />
                    </div>
                    <div class="report-check-card__content">
                      <div class="report-check-card__title">Por Anomalias</div>
                      <div class="report-check-card__desc">
                        Destaca os tipos mais recorrentes.
                      </div>
                    </div>
                  </button>

                  <button
                      type="button"
                      class="report-check-card"
                      :class="{ 'is-active': form.rankings.includes('origin') }"
                      @click="toggleRanking('origin')"
                  >
                    <div class="report-check-card__box">
                      <b-icon-check v-if="form.rankings.includes('origin')" />
                    </div>
                    <div class="report-check-card__icon">
                      <b-icon-shuffle />
                    </div>
                    <div class="report-check-card__content">
                      <div class="report-check-card__title">Por Origem</div>
                      <div class="report-check-card__desc">
                        Agrupa por: endógena, exógena, etc.
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </b-col>
          </b-row>
        </section>

        <section class="report-section">
          <div class="report-section__header">
            <div class="report-section__title">
              Opções avançadas
            </div>
            <div class="report-section__subtitle">
              Configurações adicionais
            </div>
          </div>

          <div class="report-panel">
            <b-form-checkbox class="mb-2" v-model="form.finishedDate" switch>
              Exibir apontamentos já finalizados?
            </b-form-checkbox>

            <b-form-checkbox v-model="form.reportByCriticality" switch>
              Exibir status de cores por tipo de criticidade?
              <t-help title="Utilize esta opção quando desejar informar os apontamentos por criticidade escolhida"/>
            </b-form-checkbox>
          </div>
        </section>


        <b-modal
            v-model="previewModal.visible"
            size="xl"
            centered
            hide-footer
            :title="previewModal.title"
        >
          <div class="report-preview-modal">
            <img :src="previewModal.image" :alt="previewModal.title">
          </div>
        </b-modal>

      </div>
    </template>

    <template #setting>


      <b-form-checkbox class="mt-2 mb-2" v-model="form.reportHideHeaderFooter" switch>
        Ocultar todas informações do cabeçalho e rodapé?
      </b-form-checkbox>

      <t-input-search required radio v-show="!form.reportHideHeaderFooter" v-model="form.reportHeaderPositionLogo"
                      :options="ReportPositionLogoHeaderType.list" label="Cabeçalho logomarca"/>


      <!--
              bem mais complexo, pois aqui é por empreendimento.
              <b-col cols="6">

                <t-input v-model="form.environmentSort" required stacked
                         :options="ReportEnvironmentSortType.list"
                         radio
                         label="Ordenação dos ambientes"/>

              </b-col>-->


    </template>

  </t-report-generate>

</template>
<script>
import pageDefaultMixin from "@/mixins/page/pageDefaultMixin"
import pageReportItemMixin from "@/mixins/page/pageReportItemMixin"
import ReportPositionLogoHeaderType from "@/model/types/modules/checklist/ReportPositionLogoHeaderType";
import ReportOccurrenceLayoutType from "@/model/types/modules/checklist/ReportOccurrenceLayoutType";
import ReportOccurrenceGraphicType from "@/model/types/modules/checklist/ReportOccurrenceGraphicType";
import TypeChecklistModelService from "@/services/modules/checklist/TypeChecklistModel.service";
import ReportEnvironmentSortType from "@/model/types/modules/checklist/ReportEnvironmentSortType";
import TReportOptionCard from "@/components/template/theme/t-report-option-card.vue";

export default {
  components: {TReportOptionCard},
  mixins: [pageDefaultMixin, pageReportItemMixin],
  name: 'ReportPhotoOccurrenceByEnterprise',
  data() {
    return {
      ReportPositionLogoHeaderType,
      ReportOccurrenceLayoutType,
      ReportOccurrenceGraphicType,
      ReportEnvironmentSortType,
      path: "checklist/report", //Path padrão para validação, porém página foi configurada para validação direta na mesma página (validationOtherPageId)
      form: {
        enterpriseId: this.enterpriseId,
        finishedDate: true,
        rankings:[],
        coverLayout: 10,
        reportHideHeaderFooter: false,
        reportHeaderPositionLogo: ReportPositionLogoHeaderType.type.LEFT,
        reportLayout: ReportOccurrenceLayoutType.type.ONLY_FIRST_PHOTO,
        reportGraphic: ReportOccurrenceGraphicType.type.BASIC,
        reportByCriticality: false,
        environmentSort: ReportEnvironmentSortType.type.DEFAULT,
      },
      checklistOptions: [],

      coverOptions: [
        {
          value: 10,
          title: 'Padrão',
          description: 'Capa institucional equilibrada.',
          badge: '',
          preview: 'https://static.onlaine.com.br/svgs/cover_layout/cover_layout_10.svg'
        },
        {
          value: 20,
          title: 'Logomarca grande',
          description: 'Maior destaque para a identidade visual.',
          badge: 'Premium',
          preview: 'https://static.onlaine.com.br/svgs/cover_layout/cover_layout_20.svg'
        }
      ],
      rankingOptions: [
        {value: 'discipline', text: 'Ranking por Disciplina'},
        {value: 'criticality', text: 'Ranking por Criticidade'},
        {value: 'anomalies', text: 'Ranking por Anomalias'},
        {value: 'origin', text: 'Ranking por Origem'}
      ],
      previewModal: {
        visible: false,
        title: '',
        image: ''
      }
    }
  },
  props: {
    enterpriseId: Number,
    checklistId: Number,
  },
  methods: {

    mountedPrepare() {


    },
    async mountedContinued() {


    },

    async onEnterpriseResume(resume) {

      if (this.objValid(resume.preferentialTypeChecklistId) && resume.preferentialTypeChecklistId > 0) {

        await TypeChecklistModelService.getResume("id", resume.preferentialTypeChecklistId).then(resume => {

          this.form.reportHeaderPositionLogo = resume.reportOccurrenceDefaultHeaderPositionLogo
          this.form.reportHideHeaderFooter = resume.reportOccurrenceDefaultHideHeaderFooter
          this.form.environmentSort = resume.reportOccurrenceDefaultEnvironmentSort /*NÃO É POSSÍVEL FAZER ISSO, POIS HÁ VÁRIAS DATAS DE VISTORIAS É MAIS COMPLEXO*/
          this.form.reportLayout = resume.reportOccurrenceDefaultLayout
          this.form.reportGraphic = resume.reportOccurrenceDefaultGraphic
          this.form.reportByCriticality = resume.reportOccurrenceDefaultByCriticality

          if (this.form.reportHeaderPositionLogo === '') {
            this.form.reportHeaderPositionLogo = ReportPositionLogoHeaderType.type.LEFT
          }

        })

      }

    },

    toggleRanking(value) {
      const exists = this.form.rankings.includes(value)

      if (exists) {
        this.form.rankings = this.form.rankings.filter(item => item !== value)
        return
      }

      this.form.rankings = [...this.form.rankings, value]
    },

    openPreview(option) {
      this.previewModal = {
        visible: true,
        title: option.title,
        image: option.preview
      }
    }


  },
  async mounted() {

    this.mountedPrepare()

    await this.mountedContinued()

  },
  computed: {

  },

}
</script>
